import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import $utils from '@/utils';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: 'name' };

const createState = (options = {}) => ({
    customers: [],
    customers_url: '/api/customers/',
    customers_filters: DEFAULT_FILTERS,
    customers_count: 0,
    customers_loaded: false,
    customers_loading: false,
    customers_loading_errors: null,
    cancelTokenSource: null,
    buyers_stats: null,
    buyers_stats_loading: false,
    buyers_stats_loading_errors: null,
    _storageOptions: options || {},
});

const mutations = {
    updateCustomers(state, customers) {
        state.customers = customers;
        state.customers_loaded = true;
        state.customers_loading = false;
        state.customers_loading_errors = null;
    },
    updateCustomersLoaded(state, customers_loaded) {
        state.customers_loaded = customers_loaded;
    },
    updateCustomersUrl(state, customers_url) {
        state.customers_url = customers_url;
    },
    updateCustomersFilters(state, customers_filters) {
        state.customers_filters = customers_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.customers_filters, fieldsToSave);
        }
    },
    updateCustomersLoading(state, customers_loading) {
        state.customers_loading = customers_loading;
    },
    updateCustomersLoadingErrors(state, customers_loading_errors) {
        state.customers_loading_errors = customers_loading_errors;
    },
    updateCustomersCount(state, customers_count) {
        state.customers_count = customers_count;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
    updateBuyersStats(state, buyers_stats) {
        state.buyers_stats = buyers_stats;
        state.buyers_stats_loading = false;
        state.buyers_stats_loading_errors = null;
    },
    updateBuyersStatsLoading(state, buyers_stats_loading) {
        state.buyers_stats_loading = buyers_stats_loading;
    },
    updateBuyersStatsLoadingErrors(state, buyers_stats_loading_errors) {
        state.buyers_stats_loading_errors = buyers_stats_loading_errors;
    },
};

const actions = {

    async fetchCustomers({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("core.view_customer") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateCustomersLoading', true);
        commit('updateCustomersLoadingErrors', null);
        try {
            const response = await axios.get(state.customers_url, {
                params: state.customers_filters,
                cancelToken: source.token,
            });
            commit('updateCustomers', response.data.results);
            commit('updateCustomersCount', response.data.count);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateCustomersLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateCustomersLoading', false);
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateCustomersLoaded', false);
        commit('updateCustomers', []);
        
        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateCustomersFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params.filters || {})
        });
        
        if (params.url) {
            commit('updateCustomersUrl', params.url);
        }
    },

    exportBuyersAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params && params.filtered) {
            filters = Object.assign({}, state.customers_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: state.customers_url,
            params: filters,
        }, $utils.getFilename('buyers'));
    },

    async fetchBuyersStats({ commit, dispatch, state }) {
        commit('updateBuyersStatsLoading', true);
        commit('updateBuyersStatsLoadingErrors', null);
        try {
            const statsUrl = state.customers_url + 'stats/';
            // Include current filters in the request
            const response = await axios.get(statsUrl, {
                params: state.customers_filters,
            });
            commit('updateBuyersStats', response.data);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateBuyersStatsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateBuyersStatsLoading', false);
        }
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});